import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import SuccessNotification from '../../components/ui/SuccessNotification';
import userService from '../../services/userService';
import talentService from '../../services/talentService';

const EducatorProfileManagement = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [photoPreview, setPhotoPreview] = useState(null);
  const [talentProfile, setTalentProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  // Professional Information
  const [professionalInfo, setProfessionalInfo] = useState({
    firstName: '',
    lastName: '',
    displayName: '',
    email: '',
    phone: '',
    website: ''
  });

  // Teaching Credentials
  const [credentials, setCredentials] = useState({
    yearsExperience: '',
    education: '',
    certifications: ''
  });

  // Biography and Philosophy
  const [biography, setBiography] = useState('');
  const [teachingPhilosophy, setTeachingPhilosophy] = useState('');
  const maxBioLength = 2000;
  const maxPhilosophyLength = 1000;

  // Teaching Specializations
  const [specializations, setSpecializations] = useState({
    primary: [],
    secondary: []
  });

  // Class Preferences
  const [classPreferences, setClassPreferences] = useState({
    studentCapacity: 'small',
    schedulingPreference: 'flexible',
    assessmentMethodology: 'holistic',
    communicationStyle: 'collaborative'
  });

  // Privacy Settings
  const [privacySettings, setPrivacySettings] = useState({
    visibleToStudents: true,
    visibleToAdmins: true,
    visibleToCommunity: false
  });

  // Social Links
  const [socialLinks, setSocialLinks] = useState({
    instagram: '',
    youtube: '',
    tiktok: ''
  });

  const coachingStyles = ['Classical', 'Contemporary', 'Belt', 'Mix', 'Jazz', 'Pop/Rock'];
  const expertiseAreas = ['Vocal Technique', 'Acting Through Song', 'Audition Prep', 'Music Theory', 'Performance Coaching', 'Character Development'];
  const studentLevels = ['Beginner', 'Intermediate', 'Advanced', 'Professional'];

  useEffect(() => {
    if (user && userProfile) {
      loadTalentProfile();
    }
  }, [user, userProfile]);

  const loadTalentProfile = async () => {
    try {
      setLoadingProfile(true);
      const { talent, error: fetchError } = await talentService?.getTalentByUserId(user?.id);
      
      if (fetchError) {
        console.error('Error loading talent profile:', fetchError);
      } else if (talent) {
        setTalentProfile(talent);
        setProfessionalInfo({
          firstName: talent?.first_name || '',
          lastName: talent?.last_name || '',
          displayName: talent?.display_name || '',
          email: talent?.email || '',
          phone: talent?.phone || '',
          website: talent?.website || ''
        });
        setBiography(talent?.bio || '');
        setPhotoPreview(talent?.headshot_url);
        setSocialLinks({
          instagram: talent?.instagram || '',
          youtube: talent?.youtube || '',
          tiktok: talent?.tiktok || ''
        });
      }
    } catch (err) {
      console.error('Error loading talent profile:', err);
    } finally {
      setLoadingProfile(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    if (!file?.type?.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }

    if (file?.size > 5242880) {
      setError('Image size must be less than 5MB');
      return;
    }

    setIsUploadingPhoto(true);
    setError(null);

    try {
      const { photoUrl, error: uploadError } = await userService?.uploadHeadshot(user?.id, file);

      if (uploadError) {
        throw new Error(uploadError?.message || 'Failed to upload photo');
      }

      setPhotoPreview(photoUrl);
      setSuccessMessage('Professional photo uploaded successfully!');
      setShowSuccess(true);
    } catch (err) {
      console.error('Upload photo error:', err);
      setError(err?.message || 'Failed to upload photo. Please try again.');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleSaveProfile = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Validate required fields
      if (!professionalInfo?.firstName?.trim() || !professionalInfo?.lastName?.trim()) {
        throw new Error('First name and last name are required');
      }

      const talentData = {
        first_name: professionalInfo?.firstName?.trim(),
        last_name: professionalInfo?.lastName?.trim(),
        display_name: professionalInfo?.displayName?.trim(),
        email: professionalInfo?.email?.trim(),
        phone: professionalInfo?.phone?.trim(),
        website: professionalInfo?.website?.trim(),
        bio: biography?.trim(),
        instagram: socialLinks?.instagram?.trim(),
        youtube: socialLinks?.youtube?.trim(),
        tiktok: socialLinks?.tiktok?.trim(),
        headshot_url: photoPreview
      };

      let result;
      if (talentProfile?.id) {
        // Update existing talent profile
        result = await talentService?.updateTalent(talentProfile?.id, talentData, user?.id);
      } else {
        // Create new talent profile
        result = await talentService?.createTalent({ ...talentData, user_id: user?.id }, user?.id);
      }

      if (result?.error) {
        throw new Error(result?.error?.message || 'Failed to save profile');
      }

      setSuccessMessage('Educator profile updated successfully!');
      setShowSuccess(true);
      
      // Reload profile
      await loadTalentProfile();
    } catch (err) {
      console.error('Save profile error:', err);
      setError(err?.message || 'Failed to save profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrimarySpecializationToggle = (spec) => {
    setSpecializations(prev => ({
      ...prev,
      primary: prev?.primary?.includes(spec)
        ? prev?.primary?.filter(s => s !== spec)
        : [...prev?.primary, spec]
    }));
  };

  const handleSecondarySpecializationToggle = (spec) => {
    setSpecializations(prev => ({
      ...prev,
      secondary: prev?.secondary?.includes(spec)
        ? prev?.secondary?.filter(s => s !== spec)
        : [...prev?.secondary, spec]
    }));
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header />
        <main className="container mx-auto px-6 py-8 max-w-4xl">
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-8 text-center">
            <Icon name="Lock" size={48} className="text-blue-200 mx-auto mb-4" />
            <h2 className="text-xl font-heading font-semibold text-white mb-2">
              Access Restricted
            </h2>
            <p className="text-blue-100 mb-6">
              Please log in to access your educator profile.
            </p>
            <Button
              variant="default"
              onClick={() => navigate('/login-page')}
              iconName="LogIn"
              iconPosition="left"
              className="bg-white text-indigo-900 hover:bg-gray-100"
            >
              Go to Login
            </Button>
          </div>
        </main>
      </div>
    );
  }

  if (loadingProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header />
        <main className="container mx-auto px-6 py-8 max-w-4xl">
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
            <p className="text-white">Loading profile...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Educator Profile Management - RoleCall</title>
        <meta name="description" content="Manage your educator profile with credentials, specializations, and teaching preferences" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header />
        <main className="container mx-auto px-6 py-8 max-w-4xl">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-xl text-blue-200 hover:text-white hover:bg-white hover:bg-opacity-10 transition-colors duration-200"
                aria-label="Go back"
              >
                <Icon name="ArrowLeft" size={24} />
              </button>
              <div>
                <h1 className="text-3xl font-heading font-bold text-white">
                  Educator Profile Management
                </h1>
                <p className="text-blue-100 font-body mt-1">
                  Manage your professional credentials, specializations, and teaching preferences
                </p>
              </div>
            </div>
          </div>

          {/* Success Notification */}
          {showSuccess && (
            <SuccessNotification
              message={successMessage}
              isVisible={showSuccess}
              onClose={() => setShowSuccess(false)}
            />
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 bg-opacity-90 border border-red-200 rounded-xl p-6 mb-6">
              <div className="flex items-center space-x-2">
                <Icon name="AlertTriangle" size={20} className="text-red-800" />
                <p className="text-red-800 font-medium">{error}</p>
              </div>
            </div>
          )}

          {/* Professional Information Section */}
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6 mb-6">
            <div className="flex items-center space-x-2 mb-6">
              <Icon name="Briefcase" size={24} className="text-purple-300" />
              <h2 className="text-2xl font-heading font-bold text-white">
                Professional Information
              </h2>
            </div>

            <div className="space-y-6">
              {/* Professional Photo Upload */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Professional Photo
                </label>
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    {photoPreview ? (
                      <img
                        src={photoPreview}
                        alt="Professional photo preview"
                        className="w-24 h-24 rounded-full object-cover border-4 border-purple-300"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                        <Icon name="User" size={40} className="text-white" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                      id="photo-upload"
                      disabled={isUploadingPhoto}
                    />
                    <label htmlFor="photo-upload">
                      <Button
                        as="span"
                        variant="outline"
                        className="bg-white bg-opacity-10 text-white border-white border-opacity-30 hover:bg-opacity-20 cursor-pointer"
                        iconName={isUploadingPhoto ? null : "Upload"}
                        disabled={isUploadingPhoto}
                      >
                        {isUploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                      </Button>
                    </label>
                    <p className="text-xs text-blue-200 mt-2">
                      JPG, PNG or WebP. Max size 5MB. Professional headshot recommended.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* First Name */}
                <div>
                  <Input
                    label="First Name"
                    type="text"
                    value={professionalInfo?.firstName}
                    onChange={(e) => setProfessionalInfo(prev => ({ ...prev, firstName: e?.target?.value }))}
                    className="bg-white bg-opacity-10 text-white border-white border-opacity-30 placeholder:text-blue-200"
                    required
                  />
                </div>

                {/* Last Name */}
                <div>
                  <Input
                    label="Last Name"
                    type="text"
                    value={professionalInfo?.lastName}
                    onChange={(e) => setProfessionalInfo(prev => ({ ...prev, lastName: e?.target?.value }))}
                    className="bg-white bg-opacity-10 text-white border-white border-opacity-30 placeholder:text-blue-200"
                    required
                  />
                </div>

                {/* Display Name */}
                <div className="md:col-span-2">
                  <Input
                    label="Display Name (Optional)"
                    type="text"
                    value={professionalInfo?.displayName}
                    onChange={(e) => setProfessionalInfo(prev => ({ ...prev, displayName: e?.target?.value }))}
                    placeholder="Leave blank to use First + Last Name"
                    className="bg-white bg-opacity-10 text-white border-white border-opacity-30 placeholder:text-blue-200"
                  />
                </div>

                {/* Email */}
                <div>
                  <Input
                    label="Email"
                    type="email"
                    value={professionalInfo?.email}
                    onChange={(e) => setProfessionalInfo(prev => ({ ...prev, email: e?.target?.value }))}
                    className="bg-white bg-opacity-10 text-white border-white border-opacity-30 placeholder:text-blue-200"
                  />
                </div>

                {/* Phone */}
                <div>
                  <Input
                    label="Phone"
                    type="tel"
                    value={professionalInfo?.phone}
                    onChange={(e) => setProfessionalInfo(prev => ({ ...prev, phone: e?.target?.value }))}
                    className="bg-white bg-opacity-10 text-white border-white border-opacity-30 placeholder:text-blue-200"
                  />
                </div>

                {/* Website */}
                <div className="md:col-span-2">
                  <Input
                    label="Website"
                    type="url"
                    value={professionalInfo?.website}
                    onChange={(e) => setProfessionalInfo(prev => ({ ...prev, website: e?.target?.value }))}
                    placeholder="https://yourwebsite.com"
                    className="bg-white bg-opacity-10 text-white border-white border-opacity-30 placeholder:text-blue-200"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Teaching Credentials Section */}
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6 mb-6">
            <div className="flex items-center space-x-2 mb-6">
              <Icon name="Award" size={24} className="text-purple-300" />
              <h2 className="text-2xl font-heading font-bold text-white">
                Teaching Credentials
              </h2>
            </div>

            <div className="space-y-6">
              <div>
                <Input
                  label="Years of Teaching Experience"
                  type="text"
                  value={credentials?.yearsExperience}
                  onChange={(e) => setCredentials(prev => ({ ...prev, yearsExperience: e?.target?.value }))}
                  placeholder="e.g., 10+ years"
                  className="bg-white bg-opacity-10 text-white border-white border-opacity-30 placeholder:text-blue-200"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Education Background
                </label>
                <textarea
                  value={credentials?.education}
                  onChange={(e) => setCredentials(prev => ({ ...prev, education: e?.target?.value }))}
                  rows={3}
                  className="w-full rounded-xl bg-white bg-opacity-10 text-white border border-white border-opacity-30 px-4 py-3 placeholder:text-blue-200 focus:outline-none focus:ring-2 focus:ring-purple-400"
                  placeholder="List your degrees, institutions, and relevant training..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Professional Certifications
                </label>
                <textarea
                  value={credentials?.certifications}
                  onChange={(e) => setCredentials(prev => ({ ...prev, certifications: e?.target?.value }))}
                  rows={3}
                  className="w-full rounded-xl bg-white bg-opacity-10 text-white border border-white border-opacity-30 px-4 py-3 placeholder:text-blue-200 focus:outline-none focus:ring-2 focus:ring-purple-400"
                  placeholder="List your certifications and professional memberships..."
                />
              </div>
            </div>
          </div>

          {/* Biography and Philosophy Section */}
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6 mb-6">
            <div className="flex items-center space-x-2 mb-6">
              <Icon name="FileText" size={24} className="text-purple-300" />
              <h2 className="text-2xl font-heading font-bold text-white">
                Biography & Teaching Philosophy
              </h2>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Professional Biography
                </label>
                <textarea
                  value={biography}
                  onChange={(e) => setBiography(e?.target?.value)}
                  maxLength={maxBioLength}
                  rows={6}
                  className="w-full rounded-xl bg-white bg-opacity-10 text-white border border-white border-opacity-30 px-4 py-3 placeholder:text-blue-200 focus:outline-none focus:ring-2 focus:ring-purple-400"
                  placeholder="Share your teaching background, professional achievements, and what makes your approach unique..."
                />
                <div className="flex justify-between items-center mt-2">
                  <p className="text-xs text-blue-200">
                    Include your teaching background and professional achievements
                  </p>
                  <p className="text-xs text-blue-200">
                    {biography?.length}/{maxBioLength}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Teaching Philosophy
                </label>
                <textarea
                  value={teachingPhilosophy}
                  onChange={(e) => setTeachingPhilosophy(e?.target?.value)}
                  maxLength={maxPhilosophyLength}
                  rows={4}
                  className="w-full rounded-xl bg-white bg-opacity-10 text-white border border-white border-opacity-30 px-4 py-3 placeholder:text-blue-200 focus:outline-none focus:ring-2 focus:ring-purple-400"
                  placeholder="Describe your teaching philosophy and approach to student development..."
                />
                <div className="flex justify-end mt-2">
                  <p className="text-xs text-blue-200">
                    {teachingPhilosophy?.length}/{maxPhilosophyLength}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Teaching Specializations Section */}
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6 mb-6">
            <div className="flex items-center space-x-2 mb-6">
              <Icon name="Star" size={24} className="text-purple-300" />
              <h2 className="text-2xl font-heading font-bold text-white">
                Teaching Specializations
              </h2>
            </div>

            <div className="space-y-6">
              {/* Primary Specializations */}
              <div>
                <label className="block text-sm font-medium text-white mb-3">
                  Primary Vocal Coaching Styles
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {coachingStyles?.map((style) => (
                    <button
                      key={style}
                      onClick={() => handlePrimarySpecializationToggle(style)}
                      className={`p-3 rounded-xl border-2 transition-all ${
                        specializations?.primary?.includes(style)
                          ? 'bg-purple-600 border-purple-400 text-white' :'bg-white bg-opacity-5 border-white border-opacity-20 text-blue-100 hover:bg-opacity-10'
                      }`}
                    >
                      {style}
                    </button>
                  ))}
                </div>
              </div>

              {/* Expertise Areas */}
              <div>
                <label className="block text-sm font-medium text-white mb-3">
                  Musical Theater Expertise Areas
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {expertiseAreas?.map((area) => (
                    <button
                      key={area}
                      onClick={() => handleSecondarySpecializationToggle(area)}
                      className={`p-3 rounded-xl border-2 transition-all text-left ${
                        specializations?.secondary?.includes(area)
                          ? 'bg-purple-600 border-purple-400 text-white' :'bg-white bg-opacity-5 border-white border-opacity-20 text-blue-100 hover:bg-opacity-10'
                      }`}
                    >
                      {area}
                    </button>
                  ))}
                </div>
              </div>

              {/* Student Level Preferences */}
              <div>
                <label className="block text-sm font-medium text-white mb-3">
                  Preferred Student Levels
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {studentLevels?.map((level) => (
                    <button
                      key={level}
                      className="p-3 rounded-xl border-2 bg-white bg-opacity-5 border-white border-opacity-20 text-blue-100 hover:bg-opacity-10 transition-all"
                    >
                      {level}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Class Preferences Section */}
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6 mb-6">
            <div className="flex items-center space-x-2 mb-6">
              <Icon name="Settings" size={24} className="text-purple-300" />
              <h2 className="text-2xl font-heading font-bold text-white">
                Class Preferences
              </h2>
            </div>

            <div className="space-y-6">
              {/* Student Capacity */}
              <div>
                <label className="block text-sm font-medium text-white mb-3">
                  Student Capacity Preference
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[
                    { value: 'small', label: 'Small Groups (1-5)' },
                    { value: 'medium', label: 'Medium Groups (6-15)' },
                    { value: 'large', label: 'Large Groups (16+)' }
                  ]?.map((option) => (
                    <button
                      key={option?.value}
                      onClick={() => setClassPreferences(prev => ({ ...prev, studentCapacity: option?.value }))}
                      className={`p-3 rounded-xl border-2 transition-all ${
                        classPreferences?.studentCapacity === option?.value
                          ? 'bg-purple-600 border-purple-400 text-white' :'bg-white bg-opacity-5 border-white border-opacity-20 text-blue-100 hover:bg-opacity-10'
                      }`}
                    >
                      {option?.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Scheduling Preference */}
              <div>
                <label className="block text-sm font-medium text-white mb-3">
                  Scheduling Preference
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {['flexible', 'structured', 'by-appointment']?.map((pref) => (
                    <button
                      key={pref}
                      onClick={() => setClassPreferences(prev => ({ ...prev, schedulingPreference: pref }))}
                      className={`p-3 rounded-xl border-2 transition-all capitalize ${
                        classPreferences?.schedulingPreference === pref
                          ? 'bg-purple-600 border-purple-400 text-white' :'bg-white bg-opacity-5 border-white border-opacity-20 text-blue-100 hover:bg-opacity-10'
                      }`}
                    >
                      {pref?.replace('-', ' ')}
                    </button>
                  ))}
                </div>
              </div>

              {/* Assessment Methodology */}
              <div>
                <label className="block text-sm font-medium text-white mb-3">
                  Assessment Methodology
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {['holistic', 'performance-based', 'progress-tracking']?.map((method) => (
                    <button
                      key={method}
                      onClick={() => setClassPreferences(prev => ({ ...prev, assessmentMethodology: method }))}
                      className={`p-3 rounded-xl border-2 transition-all capitalize ${
                        classPreferences?.assessmentMethodology === method
                          ? 'bg-purple-600 border-purple-400 text-white' :'bg-white bg-opacity-5 border-white border-opacity-20 text-blue-100 hover:bg-opacity-10'
                      }`}
                    >
                      {method?.replace('-', ' ')}
                    </button>
                  ))}
                </div>
              </div>

              {/* Communication Style */}
              <div>
                <label className="block text-sm font-medium text-white mb-3">
                  Communication Style
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {['collaborative', 'directive', 'supportive']?.map((style) => (
                    <button
                      key={style}
                      onClick={() => setClassPreferences(prev => ({ ...prev, communicationStyle: style }))}
                      className={`p-3 rounded-xl border-2 transition-all capitalize ${
                        classPreferences?.communicationStyle === style
                          ? 'bg-purple-600 border-purple-400 text-white' :'bg-white bg-opacity-5 border-white border-opacity-20 text-blue-100 hover:bg-opacity-10'
                      }`}
                    >
                      {style}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Social Links Section */}
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6 mb-6">
            <div className="flex items-center space-x-2 mb-6">
              <Icon name="Share2" size={24} className="text-purple-300" />
              <h2 className="text-2xl font-heading font-bold text-white">
                Social Media Links
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <Input
                  label="Instagram"
                  type="text"
                  value={socialLinks?.instagram}
                  onChange={(e) => setSocialLinks(prev => ({ ...prev, instagram: e?.target?.value }))}
                  placeholder="@username"
                  className="bg-white bg-opacity-10 text-white border-white border-opacity-30 placeholder:text-blue-200"
                />
              </div>

              <div>
                <Input
                  label="YouTube"
                  type="text"
                  value={socialLinks?.youtube}
                  onChange={(e) => setSocialLinks(prev => ({ ...prev, youtube: e?.target?.value }))}
                  placeholder="Channel URL"
                  className="bg-white bg-opacity-10 text-white border-white border-opacity-30 placeholder:text-blue-200"
                />
              </div>

              <div>
                <Input
                  label="TikTok"
                  type="text"
                  value={socialLinks?.tiktok}
                  onChange={(e) => setSocialLinks(prev => ({ ...prev, tiktok: e?.target?.value }))}
                  placeholder="@username"
                  className="bg-white bg-opacity-10 text-white border-white border-opacity-30 placeholder:text-blue-200"
                />
              </div>
            </div>
          </div>

          {/* Privacy Controls Section */}
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6 mb-6">
            <div className="flex items-center space-x-2 mb-6">
              <Icon name="Shield" size={24} className="text-purple-300" />
              <h2 className="text-2xl font-heading font-bold text-white">
                Privacy Controls
              </h2>
            </div>

            <div className="space-y-3">
              {[
                { key: 'visibleToStudents', label: 'Visible to Students', desc: 'Students can view your profile' },
                { key: 'visibleToAdmins', label: 'Visible to Administrators', desc: 'Administrators can view your profile' },
                { key: 'visibleToCommunity', label: 'Visible to RoleCall Community', desc: 'Public profile visibility' }
              ]?.map((setting) => (
                <div key={setting?.key} className="flex items-center justify-between p-4 rounded-xl bg-white bg-opacity-5 border border-white border-opacity-20">
                  <div>
                    <h3 className="text-white font-semibold">{setting?.label}</h3>
                    <p className="text-blue-200 text-sm mt-1">{setting?.desc}</p>
                  </div>
                  <button
                    onClick={() => setPrivacySettings(prev => ({ ...prev, [setting?.key]: !prev?.[setting?.key] }))}
                    className={`relative w-14 h-7 rounded-full transition-colors ${
                      privacySettings?.[setting?.key] ? 'bg-purple-600' : 'bg-gray-600'
                    }`}
                  >
                    <span
                      className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                        privacySettings?.[setting?.key] ? 'translate-x-7' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-4">
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="bg-white bg-opacity-10 text-white border-white border-opacity-30 hover:bg-opacity-20"
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleSaveProfile}
              disabled={isLoading}
              iconName={isLoading ? null : "Save"}
              className="bg-purple-600 text-white hover:bg-purple-700"
            >
              {isLoading ? 'Saving...' : 'Save Profile'}
            </Button>
          </div>
        </main>
      </div>
    </>
  );
};

export default EducatorProfileManagement;